import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageLibraryItem } from '@/hooks/runware/image-library';
import { 
  EmptyStateView,
  ImageGrid,
  LibraryHeader,
  LoadMoreIndicator,
  ErrorState
} from './components';

interface ImageLibraryContentProps {
  images: ImageLibraryItem[];
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  loadMoreRef: React.RefObject<HTMLDivElement>;
  onDeleteImage: (id: string) => void;
  onSelectImage: (image: ImageLibraryItem) => void;
  showFavorites: boolean;
  showOnlyUserImages: boolean;
  onFilterChange: {
    setShowFavorites: (value: boolean) => void;
    setShowOnlyUserImages: (value: boolean) => void;
  };
  searchTerm: string;
  parsedWidth?: number;
  parsedHeight?: number;
}

// Simplified transition - less motion overhead
const fadeTransition = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: 0.15 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.15 }
  }
};

export const ImageLibraryContent: React.FC<ImageLibraryContentProps> = ({
  images,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  loadMoreRef,
  onDeleteImage,
  onSelectImage,
  showFavorites,
  showOnlyUserImages,
  onFilterChange,
  searchTerm,
  parsedWidth,
  parsedHeight
}) => {
  // Cache images while new ones load - preserve sophisticated loading behavior
  const [displayedImages, setDisplayedImages] = useState<ImageLibraryItem[]>(images);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Preserve sophisticated transition logic
  useEffect(() => {
    if (isLoading) {
      setIsTransitioning(true);
    } else {
      setDisplayedImages(images);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, images]);

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Simplified header - removed redundant motion wrapper */}
      <LibraryHeader 
        showOnlyUserImages={showOnlyUserImages}
        showFavorites={showFavorites}
        onSetShowFavorites={onFilterChange.setShowFavorites}
        onSetShowOnlyUserImages={onFilterChange.setShowOnlyUserImages}
        isLoading={isLoading || isTransitioning}
      />
      
      {/* Simplified content area - single motion wrapper */}
      <AnimatePresence mode="wait">
        {isError ? (
          <motion.div
            key="error"
            variants={fadeTransition}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <ErrorState />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            variants={fadeTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            {/* Preserve sophisticated ImageGrid functionality */}
            <ImageGrid 
              images={displayedImages} 
              onDeleteImage={onDeleteImage} 
              onSelectImage={onSelectImage}
              isLoading={isTransitioning}
              hideStorageButton={true}
            />
            
            {/* Show empty state only when not loading and no images */}
            {!isLoading && images.length === 0 && (
              <EmptyStateView
                showFavorites={showFavorites}
                showOnlyUserImages={showOnlyUserImages}
                searchTerm={searchTerm}
                parsedWidth={parsedWidth}
                parsedHeight={parsedHeight}
              />
            )}
            
            {/* Preserve sophisticated load more behavior */}
            {(isFetchingNextPage || hasNextPage) && (
              <LoadMoreIndicator 
                isFetchingNextPage={isFetchingNextPage} 
                loadMoreRef={loadMoreRef} 
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
